/** Keeps the phone screen awake while a chauffeur is sharing their location.
 *
 *  A browser suspends geolocation the moment the screen sleeps or the app is backgrounded, which is
 *  why tracking died mid-trip. The lock is dropped by the browser whenever the page is hidden, so it
 *  has to be taken again on every return to the foreground.
 */
let sentinel = null;
// Navigation and location sharing both want the screen awake, so the lock is counted: the last
// one to let go is the one that releases it.
let holders = 0;

const supported = () => typeof navigator !== "undefined" && "wakeLock" in navigator;

async function take() {
  if (holders === 0 || !supported() || (sentinel && !sentinel.released)) return;
  try {
    sentinel = await navigator.wakeLock.request("screen");
    sentinel.addEventListener("release", () => { sentinel = null; });
  } catch {
    sentinel = null;  // denied, low battery, or not a user-visible page — nothing we can do
  }
}

const onVisible = () => { if (document.visibilityState === "visible") take(); };

export async function keepAwake() {
  holders += 1;
  document.addEventListener("visibilitychange", onVisible);
  await take();
  return supported();
}

export async function letSleep() {
  holders = Math.max(0, holders - 1);
  if (holders > 0) return;
  document.removeEventListener("visibilitychange", onVisible);
  const held = sentinel;
  sentinel = null;
  try { await held?.release(); } catch { /* already gone */ }
}

export const wakeLockSupported = supported;
